import { Link } from '@mui/material';
import { t } from 'i18next';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { TraceType } from '../../../common/enums/TraceType';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { KEY_LENGTH_OPTIONS, KEY_LENGTH_OPTION_VALIDATOR } from '../../../common/keys/constants/KeyLengths';
import { KeyCategory, getKeyCategoryLabel } from '../../../common/keys/enums/KeyCategory';
import { KeyLocation, getKeyLocationLabel } from '../../../common/keys/enums/KeyLocation';
import { KeyOperation, getKeyRoleLabel } from '../../../common/keys/enums/KeyOperation';
import { KeySource, getKeySourceLabel } from '../../../common/keys/enums/KeySource';
import { KeyType, getKeyTypeLabel } from '../../../common/keys/enums/KeyType';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { PATHS } from '../../../routing/routes';
import { ReportOutletContextType } from '../../common/contexts/ReportOutletContext';
import { Report, ReportDone, ReportFailed, ReportPending } from '../../common/models/Report';
import { ReportKey } from '../models/ReportKey';
import { ReportKeysRepository } from '../services/ReportKeysRepository';

type Props = WithRouterProps<ReportOutletContextType> & {};

type State = PaginatedTableState & {};

export class Controller extends PaginatedTableController<ReportKey, Props, State> {
  private readonly repository: ReportKeysRepository;

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.repository = new ReportKeysRepository();
  }

  componentDidMount(): void {
    super.componentDidMount();

    this.loadReportContext();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    this.onReportContextChanged(prevProps.context.report, this.props.context.report);
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.repository.cancelAllRequests();
  }

  protected doInitialDataFetch() {
    // nothing, we aren't fetching anything until we receive a ReportDone from context
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<ReportKey>> {
    const { report } = this.props.context;

    if (!(report instanceof ReportDone) || report.getKeysCount() === 0) return BLANK_PAGINATION_RESULT;

    try {
      return await this.repository.getList(report.getId(), cursorInfo, this.getFilters());
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected getFieldConfigurations(): FieldConfiguration<ReportKey>[] {
    const { report } = this.props.context;
    const output: FieldConfiguration<ReportKey>[] = [
      {
        fieldKey: 'id',
        label: t('id'),
        description: t('id'),
        columnProps: {
          maxWidth: 100,
        },
        render: (item, { tabIndex }) => (
          <Link
            tabIndex={tabIndex}
            href={UrlHelper.generatePath(PATHS.REPORT_KEY_LIFE_CYCLE, { reportId: item.getReportId().toString(), keyId: item.getId().toString() })}
            aria-label={t('a11y.report.keys.keyIdLabel', { keyId: item.getId() }) as string}
          >
            {item.getId()}
          </Link>
        ),
      },
      {
        fieldKey: 'shortDescription',
        label: t('type'),
        description: t('type'),
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getShortDescription()}</TypographyOverflow>,
      },
    ];

    if (report?.getTraceType() === TraceType.HOST_SCANNER) {
      output.push({
        fieldKey: 'locationsCount',
        label: t('locations'),
        description: t('locations'),
        columnProps: {
          maxWidth: 200,
        },
        render: (item, { hasFocus }) => (
          <TypographyOverflow hasFocus={hasFocus}>{t('foundInLocations', { count: item.getLocationsCount() })}</TypographyOverflow>
        ),
      });
    } else {
      output.push({
        fieldKey: 'operationSummary',
        label: t('usageSummary'),
        description: t('usageSummary'),
        columnProps: {
          maxWidth: 300,
        },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getOperationSummary()}</TypographyOverflow>,
      });
    }

    // Extracted like this due to the ordering of the columns
    output.push({
      fieldKey: 'information',
      label: t('keyInformation'),
      description: t('keyInformation'),
      render: (item, { hasFocus }) => {
        const information = item.getInformation();
        if (information) {
          return <TypographyOverflow hasFocus={hasFocus}>{information.join(', ')}</TypographyOverflow>;
        }
        return <BlankCell blankText={t('noInformation')} />;
      },
    });

    if (report?.getTraceType() !== TraceType.HOST_SCANNER) {
      output.push({
        fieldKey: 'firstCall',
        label: t('callSite'),
        description: t('callSite'),
        render: (item, { hasFocus }) => {
          const call = item.getFirstCall()?.getFullMethodName();
          if (call) {
            return <TypographyOverflow hasFocus={hasFocus}>{call}</TypographyOverflow>;
          }
          return <BlankCell blankText={t('unknown')} />;
        },
      });
    }
    return output;
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [
      {
        filterKey: 'isUsed',
        label: t('used'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: [
          { value: 'TRUE', label: t('true') },
          { value: 'FALSE', label: t('false') },
        ],
      },
      {
        filterKey: 'isTlsSession',
        label: t('tlsSessionKey'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: [
          { value: 'TRUE', label: t('true') },
          { value: 'FALSE', label: t('false') },
        ],
      },
      {
        filterKey: 'category',
        label: t('category'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: Object.values(KeyCategory).map((value) => ({ value, label: getKeyCategoryLabel(value) })),
      },
      {
        filterKey: 'type',
        label: t('type'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: Object.values(KeyType).map((value) => ({ value, label: getKeyTypeLabel(value) })),
      },
      {
        filterKey: 'source',
        label: t('source'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: Object.values(KeySource).map((value) => ({ value, label: getKeySourceLabel(value) })),
      },
      {
        filterKey: 'operation',
        label: t('operationType'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: Object.values(KeyOperation).map((value) => ({ value, label: getKeyRoleLabel(value) })),
      },
      {
        filterKey: 'location',
        label: t('location'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: Object.values(KeyLocation).map((value) => ({ value, label: getKeyLocationLabel(value) })),
      },
      {
        filterKey: 'length',
        label: t('keyLength'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.GE, label: t('filterLabels.isGreaterOrEqual') },
          { value: Operation.GT, label: t('filterLabels.isGreater') },
          { value: Operation.LE, label: t('filterLabels.isLessOrEqual') },
          { value: Operation.LT, label: t('filterLabels.isLess') },
        ],
        options: KEY_LENGTH_OPTIONS(),
        validator: KEY_LENGTH_OPTION_VALIDATOR,
      },
    ];
  }

  private loadReportContext() {
    // When the component mounts, consider that the previous report is null
    this.onReportContextChanged(null, this.props.context.report);
  }

  private onReportContextChanged(prevReport: Report | null, currReport: Report | null) {
    // Report hasn't changed, do nothing
    if (prevReport === currReport) return;

    if (prevReport !== null && prevReport.getId() !== currReport?.getId()) {
      // Report has changed (e.g. through a version selector)
      // Reset the table state (including stored data and pagination) and reload
      this.resetState(() => this.loadReportContext());
      return;
    }

    if (!currReport || currReport instanceof ReportPending) {
      // The report is not ready yet, display the initial loading state
      this.resetState();
    } else if (currReport instanceof ReportFailed) {
      // The report is failed, display the blank loaded state
      this.setupBlankState();
    } else if (currReport instanceof ReportDone) {
      // The report is done, load the keys
      super.doInitialDataFetch();
    }
  }
}

export const ReportKeysController = withRouter<ReportOutletContextType>(Controller);
